/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Grid } from '@mairistem/composants';

/** Un couple libellé/valeur, avec ses largeurs de colonne. */
export interface ChampFiche {
  /** Libellé sans les deux-points : ils sont ajoutés au rendu. */
  libelle: string;
  /**
   * Valeur DÉJÀ formatée par un helper de `utils/affichage` : ce composant ne
   * formate rien, il n'affichera donc jamais « Invalid Date ».
   */
  valeur?: string;
  computer?: number;
  tablet?: number;
  mobile?: number;
}

/** Une ligne de la grille. */
export interface LigneChamps {
  champs: ChampFiche[];
}

/**
 * Une section = un sous-titre optionnel + n lignes. Le titre et le séparateur
 * sont rendus par le panneau appelant (leur habillage diffère d'un bloc à
 * l'autre : le titre d'INDIVIDUS partage sa ligne avec le bouton d'édition),
 * pas par `BlocChamps`.
 */
export interface SectionChamps {
  titre?: string;
  separateur?: boolean;
  lignes: LigneChamps[];
  /**
   * Modale qui édite cette section, et par là même **ouvre un nouveau groupe**.
   *
   * Un onglet d'individu peut porter plusieurs groupes de sections, chacun avec
   * son crayon et sa modale — c'est ce qui évite une modale géante quand
   * l'onglet compte quatre sections. Les sections qui ne déclarent rien
   * rejoignent le groupe précédent ; celles du premier groupe sont éditées par
   * la modale de l'onglet (`FICHE_CONFIG.modales.individus[onglet]`).
   *
   * Absente partout ⇒ un seul groupe, comportement de la naissance et de la
   * reconnaissance.
   */
  modale?: React.ComponentType<any>;
  /**
   * Clé du dossier portant l'individu que cette section décrit.
   *
   * Absente, la section décrit l'individu de l'onglet — comportement de la
   * naissance, de la reconnaissance et du décès, où un onglet vaut un individu.
   *
   * Le MARIAGE en a besoin : ses onglets de parents montrent DEUX individus
   * (`parent1Conjoint1` et `parent2Conjoint1`), chacun avec son crayon. La clé
   * accompagne la `modale` : les deux ouvrent un groupe, et le groupe sait alors
   * quel individu il édite.
   */
  cleIndividu?: string;
  /**
   * Rang du parent (1 ou 2) transmis à `ModalParent`, quand un onglet en porte
   * plusieurs. Absent, le rang est déduit de l'onglet.
   */
  numeroParent?: 1 | 2;
}

interface BlocChampsProps {
  lignes: LigneChamps[];
  /** Style appliqué à chaque `Grid.Row` (dégagement du crayon, indentation…). */
  styleLigne?: React.CSSProperties;
}

/**
 * Rend une grille de couples libellé/valeur en lecture seule.
 *
 * Remplace le motif `<span><b>x : </b></span><span>y</span>` qui était répété
 * une vingtaine de fois dans les panneaux de la fiche. Le markup produit est
 * strictement celui d'avant : c'est ce qui permet aux snapshots de ne pas bouger.
 */
const BlocChamps = (props: BlocChampsProps) => {
  const { lignes, styleLigne } = props;

  return (
    <Grid>
      {lignes.map((ligne, index) => (
        <Grid.Row
          // Les lignes n'ont pas d'identité propre : leur position EST leur
          // identité, et la liste est figée par la configuration.
          // biome-ignore lint/suspicious/noArrayIndexKey: cf. commentaire
          key={index}
          // Les lignes qui suivent la première se collent à la précédente.
          style={index === 0 ? styleLigne : { paddingTop: 0, ...styleLigne }}
        >
          {ligne.champs.map((champ) => (
            <Grid.Column
              key={champ.libelle}
              computer={champ.computer}
              tablet={champ.tablet}
              mobile={champ.mobile}
            >
              <span>
                <b>{`${champ.libelle} : `}</b>
              </span>
              <span>{champ.valeur}</span>
            </Grid.Column>
          ))}
        </Grid.Row>
      ))}
    </Grid>
  );
};

export default BlocChamps;
